import defaults from './simulation_defaults.json';
import { irrigationAdaptiveDefaults } from '../Constants/IrrigationPresets';
import {
  addSimulationTankScopes,
  createSimulationNutrientReadings,
  simulationNutrientValue
} from './simulation_nutrition';
import { tankMeasurements } from './tank_monitoring';
import { co2FanSettingKeys, supportsCo2SettingsRelocation } from './co2_utils';

// Demo installations, not factory defaults or crop recommendations. Wire units:
// temperature/RH/VWC are tenths; EC and pH are hundredths.
const copy = (value) => JSON.parse(JSON.stringify(value));
const suffix = (index) => (index === 0 ? '' : `_${index + 1}`);
const event = (id, instance = 255) => ({
  alarm_type_id: id,
  sub_zone_or_id: instance,
  enabled: true
});
const offset = (id, value, instance = 255, positive = true) => ({
  alarm_type_id: id,
  sub_zone_or_id: instance,
  neg_offset: value,
  pos_offset: positive ? value : 0,
  neg_enabled: true,
  pos_enabled: positive
});
const globalAlarms = () => ({
  offset_alarms: [],
  event_alarms: [1, 2, 3, 3000, 3001].map((id) => event(id))
});

export function initialSimulationZones(type) {
  return Number(type) === 5155
    ? [
        { id: '0', name: 'Vegetative room' },
        { id: '1', name: 'Flower room' }
      ]
    : [];
}

function roomSettings(type, zoneId) {
  const industrial = type === 5155;
  const root = industrial || type === 5055;
  const vegetative = industrial && String(zoneId) === '0';
  const settings = {};
  ['system', 'climate', 'co2', 'ventilation', 'timing', 'lights', 'advanced'].forEach((key) => {
    settings[key] = copy(defaults[key]);
  });
  if (type === 5055) {
    settings.system.root_sense_link_fw_flags = 3;
    settings.system.root_sense_link_fw_patch = 15;
    settings.system.root_sense_link_hw_variant = 1;
  }
  if (!industrial) {
    delete settings.ventilation.circulation;
    settings.ventilation.has_circulation = false;
  }
  if ([5052, 5053, 5055, 5155].includes(type)) {
    settings.ventilation.pressure_percent_co2 = settings.ventilation.pressure_percent_day;
    settings.climate.temp_sp_co2 = settings.co2.temp_sp_co2;
    co2FanSettingKeys.forEach((key) => { settings.ventilation[key] = settings.co2[key]; });
  }
  settings.climate.humidity_sp_day = vegetative ? 65 : 60;
  settings.climate.humidity_sp_night = vegetative ? 65 : 60;
  settings.climate.temp_sp_night = type === 5052 ? 200 : 220;
  settings.timing.end_day_hour = vegetative || type === 5052 ? 24 : 18;
  // Midnight is encoded as hour zero, never 24.
  settings.timing.end_day_hour %= 24;
  settings.lights.lights_max = type === 5052 ? 70 : vegetative ? 75 : 85;
  settings.co2.co2_control = industrial ? 1 : 0;
  settings.co2.co2_sp = vegetative ? 800 : 900;
  settings.co2.room_vol_sp = industrial ? 1200 : type === 5052 ? 20 : type === 5053 ? 80 : 200;
  settings.co2.room_scale = industrial ? 1 : 0;
  settings.co2.co2_flow = industrial ? 80 : 20;
  settings.co2.exhaust_fans_flow = industrial ? 1200 : 400;
  settings.multi_out = { memo_names: [] };
  for (let port = 1; port <= 25; port += 1) {
    settings.multi_out[`out_${port}_type`] = port <= 4 ? 0 : 1;
    settings.multi_out[`out_${port}_func`] =
      (type === 5055 ? [1, 0, 31, 32] : [1, 0, 2, 4])[port - 1] ?? 19;
  }
  settings.nutrition = {
    nutrient_settings: Array.from({ length: 4 }, (_, index) => ({
      ec_setpoint: vegetative ? 180 : 220,
      ph_setpoint: 600,
      temp_setpoint: 220,
      pump_cnt_used: industrial && index === 0 ? 2 : 0,
      pumps: Array.from({ length: industrial && index === 0 ? 2 : 0 }, (_, pump) => ({
        type: 0,
        unit: 1,
        value: pump < 2 ? 50 : 0,
        name: pump < 2 ? `Base ${pump === 0 ? 'A' : 'B'}` : ''
      }))
    }))
  };
  settings.irrigation = {
    irrigation_load_error: 0,
    irrigation_settings: Array.from({ length: 4 }, (_, index) => ({
      ...copy(defaults.irrigation_setting),
      ...(root ? irrigationAdaptiveDefaults : {}),
      ...(root ? { irrigation_p1_auto_vwc_exec_gap_days: 0 } : {}),
      enable_irrigation: root && index === 0 ? 1 : 0,
      selected_tank_id: root && index === 0 ? Number(zoneId) : 255
    })),
    irrigation_events: []
  };
  settings.irrigation_tank_info = root
    ? [
        {
          tank_id: Number(zoneId),
          enabled: true,
          name: industrial ? (vegetative ? 'Vegetative feed' : 'Flower feed') : 'Feed tank',
          availible_in_irrigation_zones: [0]
        }
      ]
    : [];
  settings.alarms = {
    offset_alarms: [offset(100, 50), offset(101, 100), offset(102, 30), offset(103, 500)],
    event_alarms: [1000, 1001].map((id) => event(id))
  };
  if (root) {
    settings.alarms.offset_alarms.push(offset(2008, 1000, 0, false), offset(2009, 200, 0, false));
    settings.alarms.event_alarms.push(event(2005, 0));
  }
  if (industrial) {
    settings.alarms.offset_alarms.push(offset(200, 50, 0), offset(201, 50, 0), offset(202, 20, 0));
    settings.alarms.event_alarms.push(...[1100, 1101, 1102].map((id) => event(id, 0)));
    settings.alarms.event_alarms.push(...[2000, 2004, 2010].map((id) => event(id, Number(zoneId))));
    settings.alarms.event_alarms.push(...[2001, 2002, 2003, 2006].map((id) => event(id, 0)));
    settings.alarms.event_alarms.push(event(3200), event(3201));
  } else {
    settings.alarms.event_alarms.push(
      ...globalAlarms().event_alarms.filter((alarm) => alarm.alarm_type_id < 3000)
    );
  }
  return settings;
}

export function createSimulationModel(type = 5055, zones = []) {
  const model = {
    type: Number(type),
    rooms: {},
    global: {
      system: copy(defaults.system),
      alarms: globalAlarms(),
      hvac: [],
      ahu_info: { ahu_unit_info_cnt: 0, ahui: [] },
      ahu_unit: { ahu_unit_used_cnt: 0, ahus: [] },
      overwr_io: { functions_used_cnt: 0, oios: [] },
      user_func_sets: [],
      slaves: null,
      ext_readings: null
    }
  };
  syncSimulationZones(model, zones);
  return model;
}

export function syncSimulationZones(model, zones) {
  const rooms = model.type === 5155 ? zones : [{ id: '0' }];
  const ids = new Set(rooms.map((zone) => String(zone.id)));
  Object.keys(model.rooms).forEach((id) => {
    if (!ids.has(id)) delete model.rooms[id];
  });
  model.global.hvac.forEach((unit) => {
    if (!ids.has(String(unit.zone))) unit.is_enabled = false;
  });
  rooms.forEach(({ id, name }) => {
    if (model.rooms[id]) return;
    model.rooms[id] = roomSettings(model.type, id);
    if (model.type === 5155) {
      const slot = model.global.hvac.findIndex((unit) => !unit.is_enabled);
      const index = slot < 0 ? model.global.hvac.length : slot;
      if (index < 16)
        model.global.hvac[index] = {
          is_enabled: true,
          zone: Number(id),
          index_per_zone: 0,
          control_mode: 0,
          operational_mode: 0,
          supported_operational_modes: 0x3f,
          name: `${name || 'Room'} HVAC`
        };
    }
  });
}

// Save the same category/zone envelope used by the real settings form. No random
// rejection and no module-global state shared by unrelated simulation sessions.
export function saveSimulationSettings(model, data) {
  if (!data || typeof data !== 'object') return false;
  const categories = Object.keys(data).filter(
    (key) => !['zone', 'id', 'name', 'has_zone_data'].includes(key)
  );
  if (!categories.length) return false;
  const zone = String(data.zone ?? data.id ?? '0');
  const targets = categories.map((key) => {
    const global =
      ['hvac', 'ahu_unit', 'overwr_io', 'user_func_sets', 'slaves', 'ext_readings'].includes(key) ||
      (['alarms', 'system'].includes(key) &&
        model.type === 5155 &&
        ((data.zone == null && data.id == null) || Number(zone) >= 254));
    return global ? model.global : model.rooms[zone];
  });
  if (categories.some((key, i) => !targets[i] || !(key in targets[i]) || data[key] == null))
    return false;
  categories.forEach((key, i) => {
    const value = data[key];
    targets[i][key] = Array.isArray(value) ? copy(value) : { ...targets[i][key], ...copy(value) };
    if (key === 'ventilation' && !supportsCo2SettingsRelocation({ type: model.type, settings: targets[i] }))
      delete targets[i][key].pressure_percent_co2;
    if (key === 'system') {
      model.global.system = copy(targets[i][key]);
      Object.values(model.rooms).forEach((room) => {
        room.system = copy(targets[i][key]);
      });
    }
  });
  const room = model.rooms[zone];
  if (room && supportsCo2SettingsRelocation({ type: model.type, settings: room })) {
    // Compatibility aliases and their destination settings refer to one value.
    // Explicit destination writes win when an older CO2 copy is sent together.
    const sync = (key, category) => {
      if (Object.prototype.hasOwnProperty.call(data[category] ?? {}, key))
        room.co2[key] = room[category][key];
      else if (Object.prototype.hasOwnProperty.call(data.co2 ?? {}, key))
        room[category][key] = room.co2[key];
    };
    sync('temp_sp_co2', 'climate');
    co2FanSettingKeys.forEach((key) => sync(key, 'ventilation'));
  }
  return true;
}

export function simulationDaytime(settings, date) {
  const timing = settings.timing;
  const minute = date.getHours() * 60 + date.getMinutes();
  const start = timing.start_day_hour * 60 + timing.start_day_min;
  const end = timing.end_day_hour * 60 + timing.end_day_min;
  return start < end ? minute >= start && minute < end : minute >= start || minute < end;
}

function liveValues(model, settings, zoneId, date, nutrientReadings) {
  const values = copy(defaults.cur_values);
  const day = simulationDaytime(settings, date);
  const phase = day ? 'day' : 'night';
  const noise = Math.sin(date.getTime() / 180000 + Number(zoneId));
  const industrial = model.type === 5155;
  const root = industrial || model.type === 5055;
  values.phase = day ? 0 : 1;
  values.info_temp_sp = settings.climate[`temp_sp_${phase}`];
  values.info_humidity_sp = settings.climate[`humidity_sp_${phase}`] * 10;
  values.temp = Math.round(values.info_temp_sp + noise * 2);
  values.humidity = Math.round(values.info_humidity_sp - noise * 4);
  values.temp_leaf_current = values.temp - 10;
  // VPD in tenths of hPa, using simulated leaf temperature and air vapour pressure.
  const saturation = (temp) => 6.112 * Math.exp((17.67 * temp) / (temp + 243.5));
  values.vpd = Math.round(
    Math.max(
      0,
      saturation(values.temp_leaf_current / 10) -
        (saturation(values.temp / 10) * values.humidity) / 1000
    ) * 10
  );
  const co2On = settings.co2.co2_control === 2 || (settings.co2.co2_control === 1 && day);
  values.co2 = Math.round((co2On ? settings.co2.co2_sp : 440) + noise * 12);
  values.neg_pressure = settings.ventilation.pressure_pa;
  values.temp_heating_mat = 220;
  values.temp_heater_setpoint_info = settings.climate[`temp_sp_heater_${phase}`];
  values.rh_intake_current = 500;
  values.temp_intake_current = 210;
  values.temp_min = Math.min(settings.climate.temp_sp_day, settings.climate.temp_sp_night) - 3;
  values.temp_max = Math.max(settings.climate.temp_sp_day, settings.climate.temp_sp_night) + 3;
  values.humidity_min =
    Math.min(settings.climate.humidity_sp_day, settings.climate.humidity_sp_night) * 10 - 5;
  values.humidity_max =
    Math.max(settings.climate.humidity_sp_day, settings.climate.humidity_sp_night) * 10 + 5;
  const pad = (n) => String(n).padStart(2, '0');
  values.time = [date.getHours(), date.getMinutes(), date.getSeconds()].map(pad).join(':');
  values.date = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
  values.cur_day = date.getDate();
  values.info_lights = day ? settings.lights.lights_max : 0;
  values.lights_dim_show_cnt = settings.lights.lights_sub_zones_active_cnt;
  values.root_zone_show_cnt = root ? 1 : 0;
  values.irrigation_show_cnt = root ? 1 : 0;
  values.nutrients_show_cnt = industrial ? 1 : 0;
  for (let index = 0; index < 4; index += 1) {
    const s = suffix(index);
    values[`vol_water_content${s}`] = root && index === 0 ? Math.round(570 + noise * 5) : 0;
    values[`ec_root_zone${s}`] = root && index === 0 ? Math.round(245 - noise * 3) : 0;
    values[`temp_root_zone${s}`] = root && index === 0 ? 220 : 0;
    tankMeasurements.forEach(({ key }, measurement) => {
      values[`${key}${s}`] = simulationNutrientValue(nutrientReadings[index], measurement);
    });
    const irrigation = settings.irrigation.irrigation_settings[index];
    values[`irrigation_phase_info${s}`] = irrigation.enable_irrigation ? (day ? 2 : 3) : 0;
    values[`irrigation_status_buffer${s}`] = irrigation.enable_irrigation
      ? `irr_st_dryback|.|irr_trig|${irrigation.irrigation_p2_target_vwc_abs}.0 %`
      : '';
    const manualRemaining = Math.ceil(((model.manualIrrigationUntil?.[`${zoneId}:${index}`] ?? 0) - date.getTime()) / 1000);
    if (manualRemaining > 0) {
      values[`irrigation_status_buffer${s}`] = `irr_st_manual|${manualRemaining}|sec`;
    }
    if (index > 0)
      values[`lights_dim_val_current${s}`] =
        day && index < values.lights_dim_show_cnt ? settings.lights[`lights_max${s}`] : 0;
  }
  const hvacs = model.global.hvac.filter(
    (unit) => unit.is_enabled && String(unit.zone) === String(zoneId)
  );
  hvacs.slice(0, 3).forEach((unit, index) => {
    const s = suffix(index);
    Object.assign(values, {
      [`show_hvac_info${s}`]: 1,
      [`hvac_status${s}`]: 70,
      [`hvac_init_status${s}`]: 70,
      [`hvac_alarm_status${s}`]: 70,
      [`hvac_unit_type${s}`]: 1,
      [`hvac_temp_info${s}`]: values.temp,
      [`hvac_rh_info${s}`]: values.humidity,
      [`hvac_temp_setpoint_trans${s}`]: values.info_temp_sp,
      [`hvac_rh_setpoint_trans${s}`]: values.info_humidity_sp,
      [`hvac_op_mode_info${s}`]: unit.operational_mode
    });
  });
  values.slave_infos = industrial
    ? [{ slave_address: Number(zoneId), slave_status: 1, slave_elapsed_time: 0 }]
    : [];
  values.slave_info_count = values.slave_infos.length;
  return values;
}

function hardware(zones) {
  const slaves = zones.map(({ id }, address) => ({
    address: Number(id),
    zone: Number(id),
    capabilities: [10],
    dev_variant: 144,
    baud: 1312,
    unique_id: 1000 + Number(id),
    abilities: ['in', 'out'],
    multi_outs: [
      [33, 'Grow lights', 3],
      [31, 'Exhaust fan', 3],
      [32, 'Intake fan', 3],
      [40, 'Feed pump', 2],
      [45, 'Irrigation valve', 2],
      [50, 'Base A', 2],
      [50, 'Base B', 2],
      [100, 'Tank low switch', 8],
      [8, 'CO2 valve', 2]
    ].map(([func, memo, type], port) => ({
      id: port,
      func,
      func_index: func === 40 || func === 100 ? Number(id) : port === 6 ? 1 : 0,
      type_id: type << 4,
      overwr_select: 0,
      overwr_val_ana: 0,
      overwr_add_sett: 0,
      overwr_timeout: '00:00:00',
      zone: String(id),
      slave: Number(id),
      state: 0,
      info: 0,
      type: 0,
      memo,
      _index: address * 9 + port
    }))
  }));
  const counters = Object.fromEntries(
    [
      'interval',
      'interv_inv',
      'co2_valve',
      'event',
      'dimm_lights',
      'pump_irrigation',
      'nutrient_pump',
      'lights_up',
      'lights_down',
      'humidifier',
      'dehumidifier',
      'valve_irrigation',
      'tank_min',
      'tank_max'
    ].map((key) => [`${key}_count`, 0])
  );
  Object.assign(counters, {
    co2_valve_count: zones.length,
    dimm_lights_count: zones.length,
    pump_irrigation_count: zones.length,
    nutrient_pump_count: zones.length * 2,
    valve_irrigation_count: zones.length,
    tank_min_count: zones.length,
    circulation_count: 4,
    // Current firmware advertises the entire valve index range, independently of catalog size.
    valve_irrigation_route_count: 32
  });
  return { slaves, counters };
}

export function simulationValues(model, full, internalId, zones = [], now = new Date()) {
  syncSimulationZones(model, zones);
  const isZoned = model.type === 5155;
  const equipment = hardware(isZoned ? zones : []);
  const nutrientReadings = createSimulationNutrientReadings(now.getTime());
  const makeRoom = (zoneId) => {
    const stored = model.rooms[zoneId];
    const values = liveValues(model, stored, zoneId, now, nutrientReadings);
    const phase = values.phase === 0 ? 'day' : 'night';
    const intake = stored.ventilation[`intake_fan_ec_min_${phase}`];
    const exhaust = stored.ventilation[`exhaust_fan_ec_min_${phase}`];
    const payload = full ? copy(stored) : {};
    Object.assign(payload, {
      timestamp: now.getTime(),
      base_info_values: !isZoned,
      cur_values: values,
      nutrition: copy(stored.nutrition),
      ec_fans: {
        ec_fans_intake_out: intake,
        ec_fans_intake_rpm: intake * 30,
        ec_fans_exhaust_out: exhaust,
        ec_fans_exhaust_rpm: exhaust * 30
      },
      multi_out_values: { out_count: isZoned ? 0 : 4 },
      multi_io_infos: []
    });
    for (let port = 1; port <= 25; port += 1) {
      const fn = stored.multi_out[`out_${port}_func`];
      payload.multi_out_values[`out_${port}_state`] =
        port <= 4 ? { 0: exhaust, 1: intake, 4: values.info_lights > 0 ? 1 : 0 }[fn] ?? 0 : 0;
    }
    if (full) {
      Object.assign(payload.timing, {
        time_cont_hour: now.getHours(),
        time_cont_min: now.getMinutes(),
        time_cont_sec: now.getSeconds(),
        time_cont_day: now.getDate(),
        time_cont_month: now.getMonth() + 1,
        time_cont_year: now.getFullYear()
      });
      const enabled = model.global.hvac.flatMap((unit, index) =>
        unit.is_enabled && String(unit.zone) === String(zoneId)
          ? [event(3100, index), event(3101, index)]
          : []
      );
      const previous = payload.alarms.event_alarms;
      payload.alarms.event_alarms = previous.filter(
        (alarm) => ![3100, 3101].includes(alarm.alarm_type_id)
      );
      payload.alarms.event_alarms.push(
        ...enabled.map(
          (alarm) =>
            previous.find(
              (old) =>
                old.alarm_type_id === alarm.alarm_type_id &&
                old.sub_zone_or_id === alarm.sub_zone_or_id
            ) || alarm
        )
      );
    }
    return payload;
  };
  let data;
  if (isZoned) {
    data = {
      timestamp: now.getTime(),
      multi_io_counters: equipment.counters,
      zones: zones.map((zone) => ({ ...zone, settings: makeRoom(zone.id) }))
    };
    if (full) {
      Object.assign(data, copy(model.global), {
        slaves: copy(model.global.slaves || equipment.slaves)
      });
      data.slaves.forEach((slave) =>
        slave.multi_outs.forEach((io) => {
          const room = data.zones.find((zone) => String(zone.id) === String(io.zone))?.settings;
          if (!room) return;
          io.state =
            {
              33: room.cur_values.info_lights,
              31: room.ec_fans.ec_fans_exhaust_out,
              32: room.ec_fans.ec_fans_intake_out
            }[io.func] ?? 0;
        })
      );
      const sensorFields = [
        [1, 'temp'],
        [2, 'humidity'],
        [3, 'co2'],
        [10, 'ec_nutrient_solution'],
        [11, 'ph_nutrient_solution'],
        [12, 'temp_nutrient_solution'],
        [13, 'redox_current'],
        [14, 'dos_current'],
        [15, 'ec_root_zone'],
        [16, 'vol_water_content'],
        [17, 'temp_root_zone']
      ];
      const extrs = data.zones.flatMap((zone) =>
        sensorFields.flatMap(([type, field]) =>
          Array.from({ length: type >= 10 && type <= 14 ? 4 : 1 }, (_, index) => ({
            gcl_addr: Number(zone.id),
            port_id: 0,
            addr_sensor: type < 10 ? 48 : type < 15 ? 49 : 50,
            sensor_addr_display_type: 0,
            zone: String(zone.id),
            sub_zone: index,
            type,
            value: zone.settings.cur_values[`${field}${suffix(index)}`],
            exclude_cur_value: false
          }))
        )
      );
      data.ext_readings = model.global.ext_readings || {
        external_readings_used_cnt: extrs.length,
        extrs
      };
    }
  } else data = makeRoom('0');
  const response = { command: 'update_values', internal_id: internalId, is_zoned: isZoned, data };
  addSimulationTankScopes(response, nutrientReadings);
  return response;
}

// Historical demo traces use the same room targets and light period. They are
// illustrative, not a replay of edits or a physical irrigation/control model.
export function applySimulationHistoryProfile(sample, timestamp, model, zoneId = '0') {
  const settings = model?.rooms[zoneId];
  if (!settings) return sample;
  let day = 0;
  for (let minute = 0; minute < 60; minute += 5) {
    day += simulationDaytime(settings, new Date(timestamp - minute * 60000)) ? 1 / 12 : 0;
  }
  const blend = (key) =>
    settings.climate[`${key}_night`] * (1 - day) + settings.climate[`${key}_day`] * day;
  const noise = Math.sin(timestamp / 1800000) * 1.5;
  sample.temp = Math.round(blend('temp_sp') + noise);
  sample.humidity = Math.round(blend('humidity_sp') * 10 - noise * 2);
  sample.temp_leaf_current = sample.temp - 10;
  const sat = (temp) => 6.112 * Math.exp((17.67 * temp) / (temp + 243.5));
  sample.vpd = Math.round(
    Math.max(
      0,
      sat(sample.temp_leaf_current / 10) - (sat(sample.temp / 10) * sample.humidity) / 1000
    ) * 10
  );
  const co2Factor = settings.co2.co2_control === 2 ? 1 : settings.co2.co2_control === 1 ? day : 0;
  sample.co2 = Math.round(440 + co2Factor * (settings.co2.co2_sp - 440) + noise * 8);
  for (let index = 0; index < 4; index += 1) {
    if (index > 0 || ![5055, 5155].includes(model.type)) {
      sample[`vol_water_content${suffix(index)}`] = 0;
      sample[`ec_root_zone${suffix(index)}`] = 0;
    }
  }
  return sample;
}
